// Coordinate maths for Astronomy mode: time scales, precession, nutation,
// sidereal time, the observer's horizon, refraction and parallax. Plain
// numbers and 3x3 row-major arrays (no three.js), in double precision.
//
// Sources:
//   - Precession: IAU 1976, Lieske, Lederle, Fricke and Morando (1977), A&A 58,
//     1, as zeta, z, theta in Meeus, Astronomical Algorithms (2nd ed., 1998),
//     eq. 21.3; mean obliquity IAU 1980 (Meeus eq. 22.2).
//   - Nutation: the IAU 1980 theory (Wahr 1981; Seidelmann 1982), every term
//     of Meeus Table 22.A (amplitudes to 0.0003"), arguments from Meeus ch. 22.
//   - Sidereal time: GMST of Aoki et al. (1982), Meeus eq. 12.4, plus the
//     equation of the equinoxes (with its 0.00264" sin Omega term, IERS 1996).
//   - The observer: WGS84 ellipsoid, geodetic to geocentric as Meeus ch. 11.
//   - Refraction: Saemundsson (1986), Sky & Telescope 72, 70 (true to
//     apparent), and Bennett (1982), Journal of Navigation 35, 255 (apparent
//     to true), both scaled for pressure and temperature as Meeus ch. 16.
//   - Time: TT - UTC from the leap-second table (IERS Bulletin C) from 1972,
//     held at 69.184 s after 2017 as Horizons does for future dates; before
//     1972 the Espenak and Meeus (2006) polynomials for Delta T used by
//     NASA's eclipse pages. UTC stands in for UT1 (within 0.9 s).
//
// Left out, with their size: polar motion (under 0.5"), diurnal aberration
// (0.3"), light deflection by the Sun (0.004" at 90 degrees from it),
// UT1 - UTC (up to 0.9 s of rotation, 13"), the IAU 2000 corrections to
// precession-nutation (under 0.05" over the century).

export const DEG = Math.PI / 180;
export const ARCSEC = Math.PI / 648000;
export const J2000 = 2451545.0;
export const AU_KM = 149597870.7;
export const C_KM_S = 299792.458;
// Light time for one AU, in days.
export const LIGHT_DAYS_PER_AU = AU_KM / C_KM_S / 86400;
// The speed of light in AU per day.
export const C_AU_DAY = 1 / LIGHT_DAYS_PER_AU;

// ---- Time -----------------------------------------------------------------------

// TAI - UTC from each date on (IERS Bulletin C), as [JD UTC, seconds].
const LEAP = [
  [2441317.5, 10], [2441499.5, 11], [2441683.5, 12], [2442048.5, 13], [2442413.5, 14],
  [2442778.5, 15], [2443144.5, 16], [2443509.5, 17], [2443874.5, 18], [2444239.5, 19],
  [2444786.5, 20], [2445151.5, 21], [2445516.5, 22], [2446247.5, 23], [2447161.5, 24],
  [2447892.5, 25], [2448257.5, 26], [2448804.5, 27], [2449169.5, 28], [2449534.5, 29],
  [2450083.5, 30], [2450630.5, 31], [2451179.5, 32], [2453736.5, 33], [2454832.5, 34],
  [2456109.5, 35], [2457204.5, 36], [2457754.5, 37],
];

// Delta T (TT - UT1) in seconds before 1972, Espenak and Meeus (2006).
function deltaTBefore1972(year) {
  let t;
  if (year < 1800) { t = (year - 1820) / 100; return -20 + 32 * t * t; }
  if (year < 1860) {
    t = year - 1800;
    return 13.72 - 0.332447 * t + 0.0068612 * t ** 2 + 0.0041116 * t ** 3 - 0.00037436 * t ** 4
      + 0.0000121272 * t ** 5 - 0.0000001699 * t ** 6 + 0.000000000875 * t ** 7;
  }
  if (year < 1900) {
    t = year - 1860;
    return 7.62 + 0.5737 * t - 0.251754 * t ** 2 + 0.01680668 * t ** 3 - 0.0004473624 * t ** 4 + t ** 5 / 233174;
  }
  if (year < 1920) {
    t = year - 1900;
    return -2.79 + 1.494119 * t - 0.0598939 * t ** 2 + 0.0061966 * t ** 3 - 0.000197 * t ** 4;
  }
  if (year < 1941) { t = year - 1920; return 21.20 + 0.84493 * t - 0.0761 * t ** 2 + 0.0020936 * t ** 3; }
  if (year < 1961) { t = year - 1950; return 29.07 + 0.407 * t - t ** 2 / 233 + t ** 3 / 2547; }
  t = year - 1975;
  return 45.45 + 1.067 * t - t ** 2 / 260 - t ** 3 / 718;
}

// TT - UTC in seconds at a UTC Julian date.
export function ttMinusUtc(jdUtc) {
  if (jdUtc < LEAP[0][0]) return deltaTBefore1972(2000 + (jdUtc - J2000) / 365.25);
  let s = LEAP[0][1];
  for (let k = LEAP.length - 1; k >= 0; k--) {
    if (jdUtc >= LEAP[k][0]) { s = LEAP[k][1]; break; }
  }
  return 32.184 + s;
}

export function jdTT(jdUtc) {
  return jdUtc + ttMinusUtc(jdUtc) / 86400;
}

// ---- Matrices (3x3, row-major) ----------------------------------------------------

export function mat3() { return [1, 0, 0, 0, 1, 0, 0, 0, 1]; }

// out may be a or b.
export function mul3(a, b, out = mat3()) {
  const a0 = a[0], a1 = a[1], a2 = a[2], a3 = a[3], a4 = a[4], a5 = a[5], a6 = a[6], a7 = a[7], a8 = a[8];
  const b0 = b[0], b1 = b[1], b2 = b[2], b3 = b[3], b4 = b[4], b5 = b[5], b6 = b[6], b7 = b[7], b8 = b[8];
  out[0] = a0 * b0 + a1 * b3 + a2 * b6; out[1] = a0 * b1 + a1 * b4 + a2 * b7; out[2] = a0 * b2 + a1 * b5 + a2 * b8;
  out[3] = a3 * b0 + a4 * b3 + a5 * b6; out[4] = a3 * b1 + a4 * b4 + a5 * b7; out[5] = a3 * b2 + a4 * b5 + a5 * b8;
  out[6] = a6 * b0 + a7 * b3 + a8 * b6; out[7] = a6 * b1 + a7 * b4 + a8 * b7; out[8] = a6 * b2 + a7 * b5 + a8 * b8;
  return out;
}

export function apply3(m, v, out = [0, 0, 0]) {
  const x = v[0], y = v[1], z = v[2];
  out[0] = m[0] * x + m[1] * y + m[2] * z;
  out[1] = m[3] * x + m[4] * y + m[5] * z;
  out[2] = m[6] * x + m[7] * y + m[8] * z;
  return out;
}

export function applyT3(m, v, out = [0, 0, 0]) {
  const x = v[0], y = v[1], z = v[2];
  out[0] = m[0] * x + m[3] * y + m[6] * z;
  out[1] = m[1] * x + m[4] * y + m[7] * z;
  out[2] = m[2] * x + m[5] * y + m[8] * z;
  return out;
}

// Rotations of the coordinate frame (the vector stays, the axes turn), as in
// the Explanatory Supplement: R1 about x, R2 about y, R3 about z.
function fill(out, m0, m1, m2, m3, m4, m5, m6, m7, m8) {
  out[0] = m0; out[1] = m1; out[2] = m2; out[3] = m3; out[4] = m4; out[5] = m5; out[6] = m6; out[7] = m7; out[8] = m8;
  return out;
}
export function R1(a, out = mat3()) { const c = Math.cos(a), s = Math.sin(a); return fill(out, 1, 0, 0, 0, c, s, 0, -s, c); }
export function R2(a, out = mat3()) { const c = Math.cos(a), s = Math.sin(a); return fill(out, c, 0, -s, 0, 1, 0, s, 0, c); }
export function R3(a, out = mat3()) { const c = Math.cos(a), s = Math.sin(a); return fill(out, c, s, 0, -s, c, 0, 0, 0, 1); }
const _ra = mat3(), _rb = mat3();

// ---- Precession and nutation ------------------------------------------------------

// Mean obliquity of the ecliptic of date (IAU 1980), radians.
export function meanObliquity(jdTTv) {
  const T = (jdTTv - J2000) / 36525;
  return (84381.448 - 46.8150 * T - 0.00059 * T * T + 0.001813 * T * T * T) * ARCSEC;
}

// J2000 mean equator and equinox to the mean equator and equinox of date.
export function precessionMatrix(jdTTv, out = mat3()) {
  const t = (jdTTv - J2000) / 36525;
  const zeta = (2306.2181 * t + 0.30188 * t * t + 0.017998 * t * t * t) * ARCSEC;
  const z = (2306.2181 * t + 1.09468 * t * t + 0.018203 * t * t * t) * ARCSEC;
  const theta = (2004.3109 * t - 0.42665 * t * t - 0.041833 * t * t * t) * ARCSEC;
  mul3(R3(-z, _ra), R2(theta, _rb), out);
  return mul3(out, R3(-zeta, _ra), out);
}

// IAU 1980 nutation, Meeus Table 22.A: multiples of D, M, M', F, Omega, then
// the sine coefficient of dpsi and its T rate and the cosine coefficient of
// deps and its T rate, in 0.0001".
const NUT = [
  [0, 0, 0, 0, 1, -171996, -174.2, 92025, 8.9],
  [-2, 0, 0, 2, 2, -13187, -1.6, 5736, -3.1],
  [0, 0, 0, 2, 2, -2274, -0.2, 977, -0.5],
  [0, 0, 0, 0, 2, 2062, 0.2, -895, 0.5],
  [0, 1, 0, 0, 0, 1426, -3.4, 54, -0.1],
  [0, 0, 1, 0, 0, 712, 0.1, -7, 0],
  [-2, 1, 0, 2, 2, -517, 1.2, 224, -0.6],
  [0, 0, 0, 2, 1, -386, -0.4, 200, 0],
  [0, 0, 1, 2, 2, -301, 0, 129, -0.1],
  [-2, -1, 0, 2, 2, 217, -0.5, -95, 0.3],
  [-2, 0, 1, 0, 0, -158, 0, 0, 0],
  [-2, 0, 0, 2, 1, 129, 0.1, -70, 0],
  [0, 0, -1, 2, 2, 123, 0, -53, 0],
  [2, 0, 0, 0, 0, 63, 0, 0, 0],
  [0, 0, 1, 0, 1, 63, 0.1, -33, 0],
  [2, 0, -1, 2, 2, -59, 0, 26, 0],
  [0, 0, -1, 0, 1, -58, -0.1, 32, 0],
  [0, 0, 1, 2, 1, -51, 0, 27, 0],
  [-2, 0, 2, 0, 0, 48, 0, 0, 0],
  [0, 0, -2, 2, 1, 46, 0, -24, 0],
  [2, 0, 0, 2, 2, -38, 0, 16, 0],
  [0, 0, 2, 2, 2, -31, 0, 13, 0],
  [0, 0, 2, 0, 0, 29, 0, 0, 0],
  [-2, 0, 1, 2, 2, 29, 0, -12, 0],
  [0, 0, 0, 2, 0, 26, 0, 0, 0],
  [-2, 0, 0, 2, 0, -22, 0, 0, 0],
  [0, 0, -1, 2, 1, 21, 0, -10, 0],
  [0, 2, 0, 0, 0, 17, -0.1, 0, 0],
  [2, 0, -1, 0, 1, 16, 0, -8, 0],
  [-2, 2, 0, 2, 2, -16, 0.1, 7, 0],
  [0, 1, 0, 0, 1, -15, 0, 9, 0],
  [-2, 0, 1, 0, 1, -13, 0, 7, 0],
  [0, -1, 0, 0, 1, -12, 0, 6, 0],
  [0, 0, 2, -2, 0, 11, 0, 0, 0],
  [2, 0, -1, 2, 1, -10, 0, 5, 0],
  [2, 0, 1, 2, 2, -8, 0, 3, 0],
  [0, 1, 0, 2, 2, 7, 0, -3, 0],
  [-2, 1, 1, 0, 0, -7, 0, 0, 0],
  [0, -1, 0, 2, 2, -7, 0, 3, 0],
  [2, 0, 0, 2, 1, -7, 0, 3, 0],
  [2, 0, 1, 0, 0, 6, 0, 0, 0],
  [-2, 0, 2, 2, 2, 6, 0, -3, 0],
  [-2, 0, 1, 2, 1, 6, 0, -3, 0],
  [2, 0, -2, 0, 1, -6, 0, 3, 0],
  [2, 0, 0, 0, 1, -6, 0, 3, 0],
  [0, -1, 1, 0, 0, 5, 0, 0, 0],
  [-2, -1, 0, 2, 1, -5, 0, 3, 0],
  [-2, 0, 0, 0, 1, -5, 0, 3, 0],
  [0, 0, 2, 2, 1, -5, 0, 3, 0],
  [-2, 0, 2, 0, 1, 4, 0, 0, 0],
  [-2, 1, 0, 2, 1, 4, 0, 0, 0],
  [0, 0, 1, -2, 0, 4, 0, 0, 0],
  [-1, 0, 1, 0, 0, -4, 0, 0, 0],
  [-2, 1, 0, 0, 0, -4, 0, 0, 0],
  [1, 0, 0, 0, 0, -4, 0, 0, 0],
  [0, 0, 1, 2, 0, 3, 0, 0, 0],
  [0, 0, -2, 2, 2, -3, 0, 0, 0],
  [-1, -1, 1, 0, 0, -3, 0, 0, 0],
  [0, 1, 1, 0, 0, -3, 0, 0, 0],
  [0, -1, 1, 2, 2, -3, 0, 0, 0],
  [2, -1, -1, 2, 2, -3, 0, 0, 0],
  [0, 0, 3, 2, 2, -3, 0, 0, 0],
  [2, -1, 0, 2, 2, -3, 0, 0, 0],
];

// Nutation in longitude and obliquity, radians, and the Moon's node.
export function nutation(jdTTv, out = { dpsi: 0, deps: 0, omega: 0 }) {
  const T = (jdTTv - J2000) / 36525;
  const T2 = T * T, T3 = T2 * T;
  const D = (297.85036 + 445267.111480 * T - 0.0019142 * T2 + T3 / 189474) * DEG;
  const M = (357.52772 + 35999.050340 * T - 0.0001603 * T2 - T3 / 300000) * DEG;
  const Mp = (134.96298 + 477198.867398 * T + 0.0086972 * T2 + T3 / 56250) * DEG;
  const F = (93.27191 + 483202.017538 * T - 0.0036825 * T2 + T3 / 327270) * DEG;
  const Om = (125.04452 - 1934.136261 * T + 0.0020708 * T2 + T3 / 450000) * DEG;
  let dpsi = 0, deps = 0;
  for (let k = 0; k < NUT.length; k++) {
    const r = NUT[k];
    const arg = r[0] * D + r[1] * M + r[2] * Mp + r[3] * F + r[4] * Om;
    dpsi += (r[5] + r[6] * T) * Math.sin(arg);
    deps += (r[7] + r[8] * T) * Math.cos(arg);
  }
  out.dpsi = dpsi * 1e-4 * ARCSEC;
  out.deps = deps * 1e-4 * ARCSEC;
  out.omega = Om;
  return out;
}

// Mean equator and equinox of date to the true ones.
export function nutationMatrix(epsA, dpsi, deps, out = mat3()) {
  mul3(R1(-(epsA + deps), _ra), R3(-dpsi, _rb), out);
  return mul3(out, R1(epsA, _ra), out);
}

// ---- Sidereal time --------------------------------------------------------------

// Greenwich mean sidereal time, radians (UT1 as the clock's UTC).
export function gmst(jdUt1) {
  const d = jdUt1 - J2000;
  const T = d / 36525;
  // The whole days apart from the fraction, so the large product keeps its
  // precision: 360.98564736629 d = 360 d + 0.98564736629 d.
  const whole = Math.floor(d), frac = d - whole;
  let deg = 280.46061837 + 360 * frac + 0.98564736629 * d + 0.000387933 * T * T - T * T * T / 38710000;
  deg = ((deg % 360) + 360) % 360;
  return deg * DEG;
}

// Greenwich apparent sidereal time: GMST plus the equation of the equinoxes.
export function gast(jdUt1, dpsi, eps, omega) {
  const ee = dpsi * Math.cos(eps) + (0.00264 * Math.sin(omega) + 0.000063 * Math.sin(2 * omega)) * ARCSEC;
  let a = gmst(jdUt1) + ee;
  a %= 2 * Math.PI;
  if (a < 0) a += 2 * Math.PI;
  return a;
}

// ---- The observer --------------------------------------------------------------

const WGS84_A = 6378.137; // km
const WGS84_F = 1 / 298.257223563;

// The observer's geocentric position in km, in the true equator and equinox
// of date, given the local apparent sidereal time.
export function observerVector(latDeg, heightM, lst, out = [0, 0, 0]) {
  const phi = latDeg * DEG;
  const ba = 1 - WGS84_F;
  const u = Math.atan(ba * Math.tan(phi));
  const h = (heightM || 0) / 1000 / WGS84_A;
  const rs = ba * Math.sin(u) + h * Math.sin(phi);
  const rc = Math.cos(u) + h * Math.cos(phi);
  out[0] = WGS84_A * rc * Math.cos(lst);
  out[1] = WGS84_A * rc * Math.sin(lst);
  out[2] = WGS84_A * rs;
  return out;
}

// True equator of date to the horizon, as the three.js world frame Astronomy
// mode uses: +X east, +Y the zenith, +Z south (the camera looks along -Z,
// north). lst is the local apparent sidereal time, latDeg the geodetic
// latitude. Rows: east, up, south in equatorial-of-date components.
export function horizonMatrix(lst, latDeg, out = mat3()) {
  const phi = latDeg * DEG;
  const cl = Math.cos(lst), sl = Math.sin(lst), cp = Math.cos(phi), sp = Math.sin(phi);
  // In the frame turned by R3(lst): x to the meridian, y east, z the pole.
  // up = (cp, 0, sp), north = (-sp, 0, cp), east = (0, 1, 0).
  // R3(lst) rows: (cl, sl, 0), (-sl, cl, 0), (0, 0, 1).
  // east  = row 2 of R3       = (-sl, cl, 0)
  // up    = cp * row1 + sp * row3 = (cp cl, cp sl, sp)
  // south = sp * row1 - cp * row3 = (sp cl, sp sl, -cp)
  out[0] = -sl; out[1] = cl; out[2] = 0;
  out[3] = cp * cl; out[4] = cp * sl; out[5] = sp;
  out[6] = sp * cl; out[7] = sp * sl; out[8] = -cp;
  return out;
}

// World (east, up, south) unit vector to altitude and azimuth (from north
// through east), radians.
export function worldToAltAz(v, out = { alt: 0, az: 0 }) {
  out.alt = Math.asin(Math.max(-1, Math.min(1, v[1])));
  let az = Math.atan2(v[0], -v[2]);
  if (az < 0) az += 2 * Math.PI;
  out.az = az;
  return out;
}

export function altAzToWorld(alt, az, out = [0, 0, 0]) {
  const ca = Math.cos(alt);
  out[0] = ca * Math.sin(az);
  out[1] = Math.sin(alt);
  out[2] = -ca * Math.cos(az);
  return out;
}

// ---- Refraction -----------------------------------------------------------------

// Standard conditions: 1010 hPa, 10 C (Meeus ch. 16), factor 1.
function conditions(pressureHPa = 1010, tempC = 10) {
  return (pressureHPa / 1010) * (283 / (273 + tempC));
}

// Refraction in degrees for a true (airless) altitude in degrees,
// Saemundsson. Below -1 degree it is held at its -1 degree value, where the
// ground hides the sky anyway, so the formula never meets its pole.
export function refractionFromTrue(altDeg, pressureHPa, tempC) {
  const h = Math.max(-1, altDeg);
  if (h > 89.9) return 0;
  const r = 1.02 / Math.tan((h + 10.3 / (h + 5.11)) * DEG); // arcmin
  return Math.max(0, r) / 60 * conditions(pressureHPa, tempC);
}

// Refraction in degrees for an apparent altitude in degrees, Bennett.
export function refractionFromApparent(altDeg, pressureHPa, tempC) {
  const h = Math.max(-1, altDeg);
  if (h > 89.9) return 0;
  const r = 1 / Math.tan((h + 7.31 / (h + 4.4)) * DEG); // arcmin
  return Math.max(0, r) / 60 * conditions(pressureHPa, tempC);
}

export const apparentAltitude = (trueDeg, p, t) => trueDeg + refractionFromTrue(trueDeg, p, t);
export const trueAltitude = (appDeg, p, t) => appDeg - refractionFromApparent(appDeg, p, t);

// The same as GLSL on a world direction (+Y up), for the stars and lines.
// uRefraction scales it (0 turns it off, 1 standard air).
export const REFRACTION_GLSL = /* glsl */`
  uniform float uRefraction;
  vec3 astroRefract(vec3 d) {
    float s = clamp(d.y, -1.0, 1.0);
    float h = max(-1.0, degrees(asin(s)));
    if (uRefraction <= 0.0 || h > 89.9) return d;
    float r = 1.02 / tan(radians(h + 10.3 / (h + 5.11))) / 60.0 * uRefraction;
    float a = radians(degrees(asin(s)) + max(r, 0.0));
    vec2 hz = d.xz;
    float l = length(hz);
    if (l < 1e-6) return d;
    return vec3(hz / l * cos(a), sin(a)).xzy;
  }
`;

// ---- Vectors --------------------------------------------------------------------

export function raDecToVector(ra, dec, out = [0, 0, 0]) {
  const cd = Math.cos(dec);
  out[0] = cd * Math.cos(ra);
  out[1] = cd * Math.sin(ra);
  out[2] = Math.sin(dec);
  return out;
}

export function vectorToRaDec(v, out = { ra: 0, dec: 0 }) {
  const r = Math.hypot(v[0], v[1], v[2]);
  let ra = Math.atan2(v[1], v[0]);
  if (ra < 0) ra += 2 * Math.PI;
  out.ra = ra;
  out.dec = Math.asin(Math.max(-1, Math.min(1, v[2] / r)));
  return out;
}

export function normalize3(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  v[0] /= l; v[1] /= l; v[2] /= l;
  return v;
}

// Annual aberration of a unit direction u by the observer's velocity beta
// (v / c), to first order in beta: the direction of u + beta.
export function aberrate(u, beta, out = [0, 0, 0]) {
  out[0] = u[0] + beta[0]; out[1] = u[1] + beta[1]; out[2] = u[2] + beta[2];
  return normalize3(out);
}

// Separation of two unit vectors, radians (stable at small angles).
export function separation(a, b) {
  const cx = a[1] * b[2] - a[2] * b[1], cy = a[2] * b[0] - a[0] * b[2], cz = a[0] * b[1] - a[1] * b[0];
  return Math.atan2(Math.hypot(cx, cy, cz), a[0] * b[0] + a[1] * b[1] + a[2] * b[2]);
}
